// v3D — Editable UI Text CMS for admin.
// Lets admin browse every translatable string in the app and override the
// auto-generated translation for any of 12 non-English languages.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Loader2, Search, Save, X, RotateCcw, Bot, Edit3, Plus, Download, Upload,
    AlertCircle, FileText, CheckCircle2, RefreshCw, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";
import i18n from "../../i18n";

const ALL_LANGS = [
    { code: "hi", label: "हिन्दी (Hindi)" },
    { code: "te", label: "తెలుగు (Telugu)" },
    { code: "ta", label: "தமிழ் (Tamil)" },
    { code: "ml", label: "മലയാളം (Malayalam)" },
    { code: "kn", label: "ಕನ್ನಡ (Kannada)" },
    { code: "mr", label: "मराठी (Marathi)" },
    { code: "or", label: "ଓଡ଼ିଆ (Odia)" },
    { code: "bn", label: "বাংলা (Bengali)" },
    { code: "gu", label: "ગુજરાતી (Gujarati)" },
    { code: "ur", label: "اردو (Urdu)" },
    { code: "pa", label: "ਪੰਜਾਬੀ (Punjabi)" },
    { code: "as", label: "অসমীয়া (Assamese)" },
];

const NAMESPACES = ["", "ui", "product", "chat", "rx", "misc"];

const NS_TONE = {
    ui: "bg-violet-100 text-violet-800",
    product: "bg-emerald-100 text-emerald-800",
    chat: "bg-sky-100 text-sky-800",
    rx: "bg-rose-100 text-rose-800",
    misc: "bg-zinc-100 text-zinc-700",
};

const Cell = ({ row, lang, onSave, onReset }) => {
    const tr = row.translations?.[lang];
    const text = tr?.text ?? "";
    const overridden = !!tr?.is_manual_override;
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(text);
    const [saving, setSaving] = useState(false);
    const textareaRef = useRef(null);

    useEffect(() => {
        if (editing && textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
        }
    }, [editing]);

    useEffect(() => { setDraft(text); }, [text, editing]);

    const start = () => { setDraft(text); setEditing(true); };
    const cancel = () => { setEditing(false); setDraft(text); };
    const save = async () => {
        const v = draft.trim();
        if (!v) {
            toast.error("Translation cannot be empty — use Reset to remove override");
            return;
        }
        if (v === text && overridden) {
            setEditing(false);
            return;
        }
        try {
            setSaving(true);
            await onSave(lang, v);
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        if (!overridden) return;
        if (!window.confirm("Reset to auto-translation? This will fetch a fresh AI translation.")) return;
        try {
            setSaving(true);
            await onReset(lang);
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <div className="space-y-1" data-testid={`cms-cell-edit-${row.key_hash}-${lang}`}>
                <textarea
                    ref={textareaRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") cancel();
                        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) save();
                    }}
                    rows={Math.min(6, Math.max(2, Math.ceil(draft.length / 40)))}
                    maxLength={4000}
                    className="w-full resize-y rounded-md border border-brand-400 bg-white p-2 text-sm outline-none focus:border-brand-600"
                />
                <div className="flex items-center justify-between text-[10px] text-zinc-500">
                    <span>{draft.length}/4000 · Ctrl+Enter saves · Esc cancels</span>
                    <div className="flex gap-1">
                        <button onClick={cancel} className="rounded px-2 py-0.5 text-zinc-600 hover:bg-zinc-100" data-testid={`cms-cell-cancel-${lang}`}>
                            <X className="h-3 w-3" />
                        </button>
                        <button
                            onClick={save}
                            disabled={saving}
                            className="inline-flex items-center gap-1 rounded bg-brand-700 px-2 py-0.5 font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
                            data-testid={`cms-cell-save-${lang}`}
                        >
                            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                            Save
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!text) {
        return (
            <button
                onClick={start}
                className="inline-flex items-center gap-1 rounded-md border border-dashed border-zinc-300 px-2 py-1 text-[11px] text-zinc-500 hover:border-brand-400 hover:text-brand-700"
                data-testid={`cms-cell-add-${row.key_hash}-${lang}`}
            >
                <Plus className="h-3 w-3" /> Add
            </button>
        );
    }

    return (
        <div
            className="group flex items-start gap-1.5 cursor-pointer"
            onClick={start}
            data-testid={`cms-cell-${row.key_hash}-${lang}`}
        >
            {overridden ? (
                <span title="Manually overridden by admin" className="mt-0.5 inline-grid h-4 w-4 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-700">
                    <Edit3 className="h-2.5 w-2.5" />
                </span>
            ) : (
                <span title="Auto-translated by AI" className="mt-0.5 inline-grid h-4 w-4 shrink-0 place-items-center rounded-full bg-sky-100 text-sky-700">
                    <Bot className="h-2.5 w-2.5" />
                </span>
            )}
            <span className="min-w-0 flex-1 text-sm text-zinc-900 group-hover:text-brand-700">{text}</span>
            {overridden && (
                <button
                    onClick={(e) => { e.stopPropagation(); reset(); }}
                    title="Reset to auto-translation"
                    className="opacity-0 transition group-hover:opacity-100 text-zinc-400 hover:text-rose-600"
                    data-testid={`cms-cell-reset-${row.key_hash}-${lang}`}
                >
                    <RotateCcw className="h-3 w-3" />
                </button>
            )}
        </div>
    );
};

const ImportModal = ({ open, onClose, onDone }) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);

    if (!open) return null;

    const doPreview = async (dryRun) => {
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        form.append("dry_run", dryRun ? "true" : "false");
        try {
            setUploading(true);
            const { data } = await api.post("/admin/cms/bulk-import", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (dryRun) {
                setPreview(data);
            } else {
                toast.success(`Imported ${data.overrides_set} overrides across ${data.rows_with_overrides} keys`);
                onDone?.();
                onClose();
            }
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Import failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 backdrop-blur-sm p-4" data-testid="cms-import-modal">
            <div className="w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-2xl">
                <header className="flex items-center justify-between border-b border-zinc-100 px-6 py-4">
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-600">CSV Import</p>
                        <h3 className="text-base font-semibold text-zinc-900">Bulk import translations</h3>
                    </div>
                    <button onClick={onClose} className="rounded-full p-2 text-zinc-400 hover:bg-zinc-100" aria-label="Close" data-testid="cms-import-close">
                        <X className="h-4 w-4" />
                    </button>
                </header>
                <div className="space-y-4 px-6 py-5">
                    <div className="rounded-2xl border border-dashed border-zinc-300 bg-zinc-50/40 px-4 py-6 text-center">
                        <FileText className="mx-auto mb-2 h-6 w-6 text-zinc-400" />
                        <p className="text-sm text-zinc-600">CSV format: <span className="font-mono text-xs">source_text, namespace, hi, te, ta, ml, kn, mr, or, bn, gu, ur, pa, as</span></p>
                        <p className="mt-1 text-[10px] text-zinc-500">Empty cells are ignored. New keys are auto-registered.</p>
                        <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm font-medium hover:border-brand-400">
                            <Upload className="h-3.5 w-3.5" />
                            {file ? file.name : "Choose CSV"}
                            <input
                                type="file"
                                accept=".csv"
                                className="hidden"
                                onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
                                data-testid="cms-import-file"
                            />
                        </label>
                    </div>

                    {preview && (
                        <div className="rounded-2xl border border-zinc-100 bg-zinc-50/60 px-4 py-3" data-testid="cms-import-preview">
                            <p className="text-xs font-semibold text-zinc-700">Preview (dry run)</p>
                            <p className="mt-1 text-[11px] text-zinc-600">Rows: <strong>{preview.rows_processed}</strong> · Will override <strong>{preview.overrides_set}</strong> across <strong>{preview.rows_with_overrides}</strong> keys · New keys: <strong>{preview.new_keys}</strong></p>
                            <p className="text-[11px] text-zinc-500">Languages: {preview.langs_detected.join(", ")}</p>
                            {preview.errors?.length > 0 && (
                                <div className="mt-2 text-[11px] text-rose-700">
                                    <strong>{preview.errors.length}</strong> error(s) — first 3:
                                    <ul>{preview.errors.slice(0, 3).map((e, i) => <li key={i}>row {e.row}{e.lang ? ` (${e.lang})` : ""}: {e.error}</li>)}</ul>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-2">
                        <button
                            onClick={() => doPreview(true)}
                            disabled={!file || uploading}
                            className="rounded-full border border-zinc-200 px-4 py-2 text-sm font-medium text-zinc-700 hover:border-brand-300 disabled:opacity-50"
                            data-testid="cms-import-preview-btn"
                        >
                            {uploading ? <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> : null}
                            Preview
                        </button>
                        <button
                            onClick={() => doPreview(false)}
                            disabled={!file || uploading}
                            className="inline-flex items-center gap-1 rounded-full bg-brand-700 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-800 disabled:opacity-50"
                            data-testid="cms-import-commit-btn"
                        >
                            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                            Import & save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AdminCmsContent = () => {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [namespace, setNamespace] = useState("");
    const [overriddenOnly, setOverriddenOnly] = useState(false);
    const [selectedLangs, setSelectedLangs] = useState(["hi", "ta", "bn"]);
    const [importOpen, setImportOpen] = useState(false);
    const pageSize = 50;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/cms/keys", {
                params: {
                    namespace: namespace || undefined,
                    q: search || undefined,
                    overridden_only: overriddenOnly || undefined,
                    page,
                    page_size: pageSize,
                },
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch {
            toast.error("Could not load translations");
        } finally {
            setLoading(false);
        }
    }, [search, namespace, overriddenOnly, page]);

    useEffect(() => { load(); }, [load]);

    // Debounce search input
    useEffect(() => {
        const t = setTimeout(() => { setPage(1); load(); }, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [search]);

    const onSaveCell = async (rowIdx, lang, text) => {
        const row = items[rowIdx];
        try {
            const { data } = await api.patch(`/admin/cms/keys/${row.key_hash}`, { lang, text });
            // Optimistic update
            setItems((arr) => arr.map((r, i) => i === rowIdx
                ? { ...r, translations: { ...r.translations, [lang]: { text: data.translated_text, is_manual_override: true, overridden_at: data.overridden_at } } }
                : r
            ));
            toast.success("Saved");
            // Reload that language for the live customer-facing app.
            try { await i18n.reloadResources([lang], ["ui"]); } catch { /* non-blocking */ }
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Save failed");
        }
    };

    const onResetCell = async (rowIdx, lang) => {
        const row = items[rowIdx];
        try {
            const { data } = await api.post(`/admin/cms/keys/${row.key_hash}/reset`, { lang });
            setItems((arr) => arr.map((r, i) => i === rowIdx
                ? { ...r, translations: { ...r.translations, [lang]: { text: data.translated_text, is_manual_override: false } } }
                : r
            ));
            toast.success("Reset to auto-translation");
            try { await i18n.reloadResources([lang], ["ui"]); } catch { /* non-blocking */ }
        } catch {
            toast.error("Reset failed");
        }
    };

    const exportCsv = async () => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/export${namespace ? `?namespace=${namespace}` : ""}`;
        try {
            const token = window.localStorage.getItem("pills.token");
            const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok) throw new Error("export failed");
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = window.URL.createObjectURL(blob);
            a.download = `pills_i18n_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success("Exported");
        } catch {
            toast.error("Export failed");
        }
    };

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);

    const toggleLang = (c) => setSelectedLangs((arr) => arr.includes(c) ? arr.filter((x) => x !== c) : [...arr, c]);

    return (
        <div className="space-y-4" data-testid="admin-cms-page">
            {/* Toolbar */}
            <div className="rounded-2xl border border-zinc-100 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="relative min-w-[240px] flex-1">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search English source… (e.g., cart, refund)"
                            className="w-full rounded-lg border border-zinc-200 pl-8 pr-3 py-2 text-sm outline-none focus:border-brand-500"
                            data-testid="cms-search-input"
                        />
                    </div>
                    <label className="flex flex-col gap-1 text-xs">
                        <span className="font-semibold uppercase tracking-wider text-zinc-500">Namespace</span>
                        <select
                            value={namespace}
                            onChange={(e) => { setNamespace(e.target.value); setPage(1); }}
                            className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                            data-testid="cms-namespace-select"
                        >
                            {NAMESPACES.map((n) => <option key={n || "all"} value={n}>{n || "All"}</option>)}
                        </select>
                    </label>
                    <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-zinc-700">
                        <input
                            type="checkbox"
                            checked={overriddenOnly}
                            onChange={(e) => { setOverriddenOnly(e.target.checked); setPage(1); }}
                            className="h-3.5 w-3.5 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
                            data-testid="cms-overridden-only"
                        />
                        Overridden only
                    </label>
                    <div className="ml-auto flex gap-2">
                        <button
                            onClick={() => setImportOpen(true)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 hover:border-brand-300"
                            data-testid="cms-import-btn"
                        >
                            <Upload className="h-3.5 w-3.5" /> Import CSV
                        </button>
                        <button
                            onClick={exportCsv}
                            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 hover:border-brand-300"
                            data-testid="cms-export-btn"
                        >
                            <Download className="h-3.5 w-3.5" /> Export
                        </button>
                        <button
                            onClick={load}
                            className="rounded-full border border-zinc-200 bg-white p-2 text-zinc-500 hover:border-brand-300"
                            aria-label="Refresh"
                            data-testid="cms-refresh"
                        >
                            <RefreshCw className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>

                {/* Language column toggle */}
                <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="cms-lang-toggles">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Show columns:</span>
                    {ALL_LANGS.map((lg) => (
                        <button
                            key={lg.code}
                            onClick={() => toggleLang(lg.code)}
                            className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition ${
                                selectedLangs.includes(lg.code)
                                    ? "border-brand-600 bg-brand-600 text-white"
                                    : "border-zinc-200 bg-white text-zinc-700 hover:border-brand-300"
                            }`}
                            data-testid={`cms-toggle-lang-${lg.code}`}
                        >
                            {lg.code}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-2xl border border-zinc-100 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-zinc-100" data-testid="cms-table">
                    <thead className="bg-zinc-50/60 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                        <tr>
                            <th className="px-4 py-3" style={{ minWidth: "280px" }}>Source (English)</th>
                            {selectedLangs.map((c) => {
                                const l = ALL_LANGS.find((x) => x.code === c);
                                return <th key={c} className="px-4 py-3" style={{ minWidth: "240px" }}>{l?.label || c}</th>;
                            })}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100 text-sm">
                        {loading ? (
                            <tr><td colSpan={1 + selectedLangs.length} className="px-4 py-12 text-center text-zinc-500"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={1 + selectedLangs.length} className="px-4 py-12 text-center text-zinc-500">No keys match these filters.</td></tr>
                        ) : (
                            items.map((row, idx) => (
                                <tr key={row.key_hash} className="hover:bg-zinc-50/40" data-testid={`cms-row-${row.key_hash}`}>
                                    <td className="px-4 py-3 align-top">
                                        <div className="space-y-1">
                                            <p className="font-medium text-zinc-900">{row.source_text}</p>
                                            <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${NS_TONE[row.namespace] || NS_TONE.misc}`}>
                                                {row.namespace}
                                            </span>
                                        </div>
                                    </td>
                                    {selectedLangs.map((c) => (
                                        <td key={c} className="px-4 py-3 align-top">
                                            <Cell
                                                row={row}
                                                lang={c}
                                                onSave={(lang, text) => onSaveCell(idx, lang, text)}
                                                onReset={(lang) => onResetCell(idx, lang)}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                {/* Pagination */}
                <div className="flex items-center justify-between border-t border-zinc-100 bg-zinc-50/60 px-4 py-2 text-xs text-zinc-600">
                    <span>
                        Showing {items.length} of {total} keys · page {page} of {totalPages}
                    </span>
                    <div className="flex gap-1">
                        <button
                            disabled={page <= 1}
                            onClick={() => setPage((p) => p - 1)}
                            className="inline-flex items-center gap-1 rounded-md border border-zinc-200 px-2 py-1 disabled:opacity-50"
                            data-testid="cms-pagination-prev"
                        >
                            <ChevronLeft className="h-3 w-3" /> Prev
                        </button>
                        <button
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => p + 1)}
                            className="inline-flex items-center gap-1 rounded-md border border-zinc-200 px-2 py-1 disabled:opacity-50"
                            data-testid="cms-pagination-next"
                        >
                            Next <ChevronRight className="h-3 w-3" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Footer help */}
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-zinc-100 bg-zinc-50/40 px-4 py-3 text-xs text-zinc-600">
                <span className="inline-flex items-center gap-1.5"><Bot className="h-3 w-3 text-sky-700" /> auto-translated</span>
                <span className="inline-flex items-center gap-1.5"><Edit3 className="h-3 w-3 text-amber-700" /> manually overridden</span>
                <span className="ml-auto inline-flex items-center gap-1"><AlertCircle className="h-3 w-3 text-zinc-500" /> Click any cell to edit. Customer pages refresh within ~1 navigation.</span>
            </div>

            <ImportModal open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />
        </div>
    );
};

const AdminCms = () => (
    <AdminAuthGuard>
        <AdminLayout title="UI Text CMS" subtitle="Edit any user-facing string in any of 12 languages — overrides survive AI re-translation">
            <AdminCmsContent />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminCms;
